// Données des cartes greybox du campus LaCaze (blockout, navigation only).
// Cartes ASCII (cf. palette dans greyboxTileset.ts) + portes + placements de Kazis, par lieu.
// Légende : '#' mur · '.' herbe · ',' béton (cour) · '_' sol intérieur · 'D' porte · 'C' terrain · 'T' arbre.
import { Direction } from 'grid-engine'
import { SceneName } from '../enums/SceneNames'
import type { ThemeName } from './themes'
import type { MoodKey } from '../../sim/moods'
import { affordancesIn } from '../../data/affordances'

export interface DoorDef { x: number; y: number; to: SceneName }
/** Prop interactif : tuile (sur un meuble) qui, tapée, applique des deltas d'humeur au joueur. */
export interface Interaction { x: number; y: number; label: string; effects: Partial<Record<MoodKey, number>>; objectId?: string; blurb?: string; coCreators?: string[] }
export interface KaziPlacement { x: number; y: number; facing?: Direction }
/** Étiquette (cadre + nom) posée sur la carte pour que le joueur reconnaisse son collège.
 *  x/y en tuiles (centre du texte) ; `kind` choisit le style (bâtiment, zone, ou petite porte). */
export interface MapLabel { x: number; y: number; text: string; kind?: 'building' | 'zone' | 'door' }

export interface LocationConfig {
    id: SceneName
    /** lignes ASCII (longueurs égalisées automatiquement par la scène) */
    ascii: string[]
    /** tuile de spawn du joueur */
    spawn: { x: number; y: number }
    /** portes (placées aux tuiles 'D') */
    doors: DoorDef[]
    /** Kazis du lieu : id de personnage -> position. Doit matcher characters.json (homeLocation = id du lieu). */
    kazis?: Record<string, KaziPlacement>
    /** thème de rendu : 'greybox' (défaut) · 'tinyTown' (campus) · 'indoors' (intérieurs Kenney). */
    theme?: ThemeName
    /** étiquettes posées sur la carte (noms des bâtiments/zones/portes). */
    labels?: MapLabel[]
    /** props interactifs (tap -> action + humeurs du joueur). */
    interactions?: Interaction[]
    /** bannière "coming soon" fixe au centre de l'écran (ex : carte vacances en développement). */
    banner?: string
}

// --- Helper : pièce intérieure rectangulaire (murs bordure, sol intérieur, une porte) ---
function room(width: number, height: number, door: { x: number; y: number }): string[] {
    const rows: string[] = []
    for (let y = 0; y < height; y++) {
        if (y === 0 || y === height - 1) rows.push('#'.repeat(width))
        else rows.push('#' + '_'.repeat(width - 2) + '#')
    }
    const r = rows[door.y]
    rows[door.y] = r.substring(0, door.x) + 'D' + r.substring(door.x + 1)
    return rows
}

// Pose des meubles sur une pièce. Items: [x, y, char]. N'écrase QUE le sol '_'
// (jamais murs/portes). À toi d'éviter les cases de spawn/Kazis. Chars: B bureau, K table,
// c chaise, S étagère, b lit, p plante.
function furnish(rows: string[], items: Array<[number, number, string]>): string[] {
    const g = rows.map(r => r.split(''))
    for (const [x, y, c] of items) {
        if (g[y] && g[y][x] === '_') g[y][x] = c
    }
    return g.map(r => r.join(''))
}

// --- Overworld : campus vu du dessus (32 large × 37 haut), calqué sur la vue SATELLITE ---
// Disposition réelle du Collège Jean Lacaze : entrée/portail au nord (Rue du Collège) ; grand
// PARKING au nord-est ; BÂTIMENT PRINCIPAL (admin/vie sco/CDI/infirmerie/profs) au nord-ouest avec
// 5 portes ; bâtiment de SALLES de classe à l'est ; grande COUR au centre ; CANTINE (SO), GYMNASE (S)
// et TERRAIN DE SPORT ('C', SE) au sud. Bordure = haie 'H'. Arbres 'T', buissons 't'.
// Rendu via le thème 'tinyTown' (vraies tuiles Kenney + auto-tiling des bâtiments).
const CAMPUS_ROWS: string[] = [
    'HHHHHHHHHHHHHHHHHHHHHHH..HHHHHHH', // 0  Rue du Collège (haie) + portail @ (23-24,0)
    'H..............................H', // 1
    'H..T...T.....T.....,,,,,,,,,,,.H', // 2  PARKING (NE) @ cols 19-29
    'H..................,,,,,,,,,,,.H', // 3
    'H.###############..,,,,,,,,,,,.H', // 4  BÂTIMENT PRINCIPAL (NO) @ cols 2-16
    'H.###############T.,,,,,,,,,,,TH', // 5
    'H.###############..,,,,,,,,,,,.H', // 6
    'H.###############..,,,,,,,,,,,.H', // 7
    'H.###############..,,,,,,,,,,,.H', // 8
    'H.###############T.,,,,,,,,,,,.H', // 9
    'H.###############..,,,,,,,,,,,.H', //10
    'H.#D##D##D##D##D#..............H', //11  portes principal @ x=3,6,9,12,15
    'H,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,H', //12  COUR DE RÉCRÉATION (dallage)
    'H,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,H', //13  (spawn joueur @ 16,13)
    'H,,,,,,,,,,,,,,,,,,,#########,,H', //14  BÂTIMENT SECONDAIRE / SALLES @ cols 20-28
    'H,,,,,,,,,,,,,,,,,,,#########,,H', //15
    'H,,,,,,,,,,,,,,,,,,,#########,,H', //16
    'H,,,,,,,,,,,,,,,,,,,#########,,H', //17
    'H,,,,,,,,,,,,,,,,,,,#########,,H', //18
    'H,,,,,,,,,,,,,,,,,,,#D##D##D#,,H', //19  portes salles A/B/C @ (21,19)/(24,19)/(27,19)
    'H,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,H', //20
    'H,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,H', //21
    'H,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,H', //22
    'H,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,H', //23
    'H,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,H', //24
    'H,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,H', //25
    'H,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,H', //26
    'H,,,,,,,,,,,,,,,,,,,,,,,,,,,,,,H', //27
    'H.###D####.####D#####..........H', //28  CANTINE porte @ (5,28) · GYMNASE porte @ (15,28)
    'H.########.##########..CCCCCCC.H', //29  TERRAIN DE SPORT ('C', SE)
    'H.########.##########..CCCCCCC.H', //30
    'H.########.##########..CCCCCCC.H', //31
    'H.########.##########..CCCCCCC.H', //32
    'H.########.##########..CCCCCCC.H', //33
    'H.########.##########..........H', //34
    'H..........##########t...T.....H', //35
    'HHHHHHHHHHHHHHHHHHHHHHHHHHHHHHHH', //36  haie sud
]

// Props interactifs d'un lieu, dérivés du catalogue partagé (SSOT, cf. data/affordances.ts)
// — le serveur applique les mêmes effets et y route les agents selon leurs besoins.
const props = (loc: SceneName): Interaction[] =>
    affordancesIn(loc).map(a => ({ x: a.tile.x, y: a.tile.y, label: a.label, effects: a.effects }))

// --- Configs des lieux ---
export const LOCATIONS: LocationConfig[] = [
    {
        id: SceneName.Campus,
        theme: 'tinyTown',
        ascii: CAMPUS_ROWS,
        spawn: { x: 16, y: 13 },
        doors: [
            { x: 3, y: 11, to: SceneName.Administration },
            { x: 6, y: 11, to: SceneName.VieScolaire },
            { x: 9, y: 11, to: SceneName.Cdi },
            { x: 12, y: 11, to: SceneName.Infirmerie },
            { x: 15, y: 11, to: SceneName.SalleProfs },
            { x: 21, y: 19, to: SceneName.Classroom },
            { x: 24, y: 19, to: SceneName.ClassroomB },
            { x: 27, y: 19, to: SceneName.ClassroomC },
            { x: 5, y: 28, to: SceneName.Cantine },
            { x: 15, y: 28, to: SceneName.Gymnase },
        ],
        labels: [
            // bâtiments
            { x: 8, y: 7, text: 'Bâtiment principal', kind: 'building' },
            { x: 24, y: 16, text: 'Salles de classe', kind: 'building' },
            { x: 5, y: 31, text: 'Cantine', kind: 'building' },
            { x: 15, y: 32, text: 'Gymnase', kind: 'building' },
            // zones
            { x: 24, y: 6, text: 'Parking', kind: 'zone' },
            { x: 10, y: 21, text: 'Cour de récréation', kind: 'zone' },
            { x: 26, y: 28, text: 'Terrain de sport', kind: 'zone' },
            // portes du bâtiment principal (au-dessus de chaque porte) + classe
            { x: 3, y: 10, text: 'Admin', kind: 'door' },
            { x: 6, y: 9, text: 'Vie sco', kind: 'door' },
            { x: 9, y: 10, text: 'CDI', kind: 'door' },
            { x: 12, y: 9, text: 'Infirm', kind: 'door' },
            { x: 15, y: 10, text: 'Profs', kind: 'door' },
            { x: 21, y: 18, text: 'Salle A', kind: 'door' },
            { x: 24, y: 18, text: 'Salle B', kind: 'door' },
            { x: 27, y: 18, text: 'Salle C', kind: 'door' },
        ],
    },
    {
        // ADMINISTRATION : bureau du principal (bibliothèque), gestion (commodes), accueil (comptoir).
        id: SceneName.Administration,
        theme: 'indoors',
        ascii: [
            '####W####h###W####',
            '#OOOO_________ddA#',
            '#________________#',
            '#_BB__________BB_#',
            '#________________#',
            '#a______RR_______#',
            '#________________#',
            '#________________#',
            '#q_____c_c_cq____#',
            '#________________#',
            '#________________#',
            '#########D########',
        ],
        spawn: { x: 9, y: 9 },
        doors: [{ x: 9, y: 11, to: SceneName.Campus }],
        kazis: {
            'principal-robin': { x: 2, y: 4, facing: Direction.DOWN },
            'adjointe-mothes': { x: 5, y: 4, facing: Direction.DOWN },
            'gestionnaire-buc': { x: 8, y: 4, facing: Direction.DOWN },
            'gestion-pougnaud': { x: 14, y: 4, facing: Direction.DOWN },
        },
        labels: [
            { x: 3, y: 6, text: 'Direction', kind: 'zone' },
            { x: 9, y: 6, text: 'Accueil', kind: 'zone' },
            { x: 14, y: 6, text: 'Gestion', kind: 'zone' },
        ],
    },
    {
        // VIE SCOLAIRE : bureaux CPE + psy + AS, salle d'attente, comptoir, commodes de dossiers.
        id: SceneName.VieScolaire,
        theme: 'indoors',
        ascii: [
            '####W####h###W####',
            '#d______________d#',
            '#________________#',
            '#__B___B___B___B_#',
            '#________________#',
            '#________________#',
            '#_______RR_______#',
            '#________________#',
            '#q_ccc_____ccc__q#',
            '#________________#',
            '#________________#',
            '#########D########',
        ],
        spawn: { x: 9, y: 9 },
        doors: [{ x: 9, y: 11, to: SceneName.Campus }],
        kazis: {
            'cpe-dalens': { x: 3, y: 4, facing: Direction.DOWN },
            'cpe-dobler': { x: 7, y: 4, facing: Direction.DOWN },
            'psy-courdier': { x: 11, y: 4, facing: Direction.DOWN },
            'assistante-sociale-sainte-marie': { x: 15, y: 4, facing: Direction.DOWN },
        },
        labels: [
            { x: 5, y: 5, text: 'Bureaux CPE', kind: 'zone' },
            { x: 12, y: 9, text: 'Attente', kind: 'zone' },
        ],
    },
    {
        // INFIRMERIE : lits de repos, bureau + armoire à pharmacie + évier, coin attente.
        id: SceneName.Infirmerie,
        theme: 'indoors',
        ascii: [
            '######h######',
            '#___________#',
            '#b_q______BA#',
            '#___________#',
            '#b________E_#',
            '#___________#',
            '#___cc______#',
            '#___________#',
            '######D######',
        ],
        spawn: { x: 6, y: 6 },
        doors: [{ x: 6, y: 8, to: SceneName.Campus }],
        kazis: {
            'infirmiere-labour': { x: 9, y: 2, facing: Direction.DOWN },
        },
        labels: [
            { x: 2, y: 3, text: 'Repos', kind: 'zone' },
            { x: 10, y: 3, text: 'Soins', kind: 'zone' },
        ],
        interactions: props(SceneName.Infirmerie),
    },
    {
        // CANTINE : cuisine (fours/évier/frigo/sacs) en haut, comptoir self, réfectoire (tables) en bas.
        id: SceneName.Cantine,
        theme: 'indoors',
        ascii: [
            '#######W###h##W#######',
            '#_VV_E_Fm_____mF_EVV_#', // cuisine : fours, évier, frigo, sacs
            '#_KK_________________#', // plans de travail
            '#_UUUUUUUU__UUUUUUUU_#', // comptoir du self (passage central)
            '#____________________#',
            '#___c__c______c__c___#', // réfectoire : tables + chaises (allée centrale)
            '#___K__K______K__K___#',
            '#___c__c______c__c___#',
            '#___c__c______c__c___#',
            '#___K__K______K__K___#',
            '#___c__c______c__c___#',
            '#___c__c______c__c___#',
            '#_q_K__K______K__K_q_#',
            '#___c__c______c__c___#',
            '###########D##########',
        ],
        spawn: { x: 11, y: 5 },
        doors: [{ x: 11, y: 14, to: SceneName.Campus }],
        kazis: {
            'chef-cuisine-lezin': { x: 4, y: 1, facing: Direction.DOWN },
            'agent-cantine-1': { x: 6, y: 2, facing: Direction.DOWN },
            'agent-cantine-2': { x: 15, y: 2, facing: Direction.DOWN },
        },
        labels: [
            { x: 4, y: 2, text: 'Cuisine', kind: 'zone' },
            { x: 11, y: 4, text: 'Self', kind: 'zone' },
            { x: 11, y: 10, text: 'Réfectoire', kind: 'zone' },
        ],
        interactions: props(SceneName.Cantine),
    },
    {
        // SALLE DE CLASSE : tableau (mur), bureau du prof, rangées de tables élèves, étagères.
        id: SceneName.Classroom,
        theme: 'indoors',
        // Open floor: students sit in a 4×7 grid (odd cols / even rows), every seat ringed by aisles.
        ascii: [
            '##W##P#P#P###W##',
            '#______B_______#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#q____________q#',
            '#______________#',
            '########D#######',
        ],
        spawn: { x: 8, y: 10 },
        doors: [{ x: 8, y: 11, to: SceneName.Campus }],
        kazis: {
            'staff-lajoux': { x: 8, y: 1, facing: Direction.DOWN },
            'staff-yeche': { x: 5, y: 1, facing: Direction.DOWN },
            'staff-pierron': { x: 11, y: 1, facing: Direction.DOWN },
        },
        labels: [
            { x: 5, y: 1, text: 'Tableau', kind: 'zone' },
        ],
        interactions: props(SceneName.Classroom),
    },
    {
        // SALLE B (multi-classe) : même géométrie que la salle A, déco variée (banderole + horloge).
        id: SceneName.ClassroomB,
        theme: 'indoors',
        ascii: [
            '##W#N#N#h####W##',
            '#______B_______#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#p____________p#',
            '#______________#',
            '########D#######',
        ],
        spawn: { x: 8, y: 10 },
        doors: [{ x: 8, y: 11, to: SceneName.Campus }],
        labels: [
            { x: 5, y: 1, text: 'Tableau', kind: 'zone' },
        ],
        interactions: props(SceneName.ClassroomB),
    },
    {
        // SALLE C (multi-classe) : même géométrie, déco posters + fenêtres.
        id: SceneName.ClassroomC,
        theme: 'indoors',
        ascii: [
            '##W##P##P##W#h##',
            '#______B_______#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#______________#',
            '#q____________p#',
            '#______________#',
            '########D#######',
        ],
        spawn: { x: 8, y: 10 },
        doors: [{ x: 8, y: 11, to: SceneName.Campus }],
        labels: [
            { x: 5, y: 1, text: 'Tableau', kind: 'zone' },
        ],
        interactions: props(SceneName.ClassroomC),
    },
    {
        // CDI : rayonnages en U, coin informatique (bureaux), coin lecture (tables), coin détente
        // (canapé/fauteuil) et accueil (comptoir + documentaliste).
        id: SceneName.Cdi,
        theme: 'indoors',
        ascii: [
            '#####W####h###W#####',
            '#_OOOOOOOOOOOOOOOO_#', // rayonnages (haut)
            '#__________________#',
            '#O____________BBB_O#', // coin informatique (bureaux)
            '#O____________ccc_O#',
            '#O____K__K_K______O#', // coin lecture (tables + chaises)
            '#O____c__c_c______O#',
            '#O________________O#',
            '#O____K__K_K______O#',
            '#O____c__c_c______O#',
            '#_q________________#',
            '#_oo_a__RR____g_q__#', // détente (canapé/fauteuil) + accueil (comptoir)
            '#__________________#',
            '##########D#########',
        ],
        spawn: { x: 10, y: 11 },
        doors: [{ x: 10, y: 13, to: SceneName.Campus }],
        kazis: {
            'documentaliste': { x: 9, y: 10, facing: Direction.DOWN },
        },
        labels: [
            { x: 15, y: 3, text: 'Informatique', kind: 'zone' },
            { x: 8, y: 6, text: 'Coin lecture', kind: 'zone' },
        ],
        interactions: props(SceneName.Cdi),
    },
    {
        // GYMNASE : grand terrain ouvert, tribunes (bancs) sur les côtés, rack matériel + casiers,
        // bureau EPS, banderoles murales.
        id: SceneName.Gymnase,
        theme: 'indoors',
        ascii: [
            '#####N##W##N##W#N#####',
            '#SSS______________LLL#',
            '#____________________#',
            '#____________________#',
            '#c__________________c#',
            '#c__________________c#',
            '#c__________________c#',
            '#c__________________c#',
            '#c__________________c#',
            '#c__________________c#',
            '#c__________________c#',
            '#c__________________c#',
            '#____________________#',
            '#_B_______________g__#',
            '#____________________#',
            '###########D##########',
        ],
        spawn: { x: 11, y: 13 },
        doors: [{ x: 11, y: 15, to: SceneName.Campus }],
        kazis: {
            'prof-eps-1': { x: 10, y: 2, facing: Direction.DOWN },
            'prof-eps-2': { x: 13, y: 12, facing: Direction.DOWN },
        },
        labels: [
            { x: 11, y: 8, text: 'Gymnase', kind: 'zone' },
            { x: 2, y: 2, text: 'Matériel', kind: 'zone' },
        ],
        interactions: props(SceneName.Gymnase),
    },
    {
        // SALLE DES PROFS : coin détente (canapés), kitchenette, grande table, casiers.
        id: SceneName.SalleProfs,
        theme: 'indoors',
        ascii: [
            '####W###h##W####',
            '#___________UF_#',
            '#oo_a__________#',
            '#______cc_____q#',
            '#______KK______#',
            '#______cc______#',
            '#______________#',
            '#______________#',
            '#LLL________LLL#',
            '#______________#',
            '########D#######',
        ],
        spawn: { x: 8, y: 8 },
        doors: [{ x: 8, y: 10, to: SceneName.Campus }],
        kazis: {
            'prof-salle-1': { x: 4, y: 3, facing: Direction.DOWN },
            'prof-salle-2': { x: 8, y: 6, facing: Direction.DOWN },
        },
        labels: [
            { x: 8, y: 7, text: 'Salle des profs', kind: 'zone' },
        ],
        interactions: props(SceneName.SalleProfs),
    },
    // Carte "Hors des cours" (coming soon). Quand l'école est fermée (nuit / week-end / vacances), le
    // serveur y rassemble TOUS les élèves au lieu de les laisser traîner à la cantine (cf. timetable.ts +
    // locations.ts). Pas de porte : on n'y va pas à pied. QA : getScene('campus').scene.switch('holiday').
    {
        id: SceneName.Holiday,
        theme: 'tinyTown',
        ascii: [
            'HHHHHHHHHHHHHHHHHH',
            'H................H',
            'H................H',
            'H................H',
            'H................H',
            'H................H',
            'H................H',
            'H................H',
            'H................H',
            'H................H',
            'H................H',
            'H................H',
            'HHHHHHHHHHHHHHHHHH',
        ],
        spawn: { x: 8, y: 6 },
        doors: [],
        labels: [
            { x: 8, y: 2, text: 'Hors des cours', kind: 'zone' },
        ],
        banner: 'École fermée 🏫\nLes élèves vivent leur vie en dehors des cours.\nSuivre leur quotidien hors les murs : bientôt 🚧',
    },
]
